import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  Chip,
  HelpTip,
  Icon,
  ROOM_ADMIN_ROLE_LABELS,
  SellerOwnershipWarning,
  cn,
  containerClass,
} from '@dataroom/shared';
import {
  useRoom,
  useRoomProcessingQueue,
  useRoomQuestions,
  useRoomSellers,
  useSession,
  useTenantPlan,
} from '@/api/hooks';
import { TrialGuide } from '@/components/FirstRunGuides';
import { ChecklistSourceDialog } from '@/components/ChecklistSourceDialog';
import { InviteSellerDialog } from '@/components/InviteSellerDialog';
import { PhaseReleaseFlow, ReleasePromptOnOpen } from '@/components/PhaseReleaseFlow';
import { RoomNameHeading } from '@/components/RoomNameHeading';
import { isUnauthorized } from '@/lib/checklist';
import { contractsEnabled } from '@/lib/features';
import { LIFECYCLE_LABELS, lifecycleChip, type RoomLocationState } from '@/lib/room';
import { processingSummary } from '@/lib/processing-queue';
import { questionsNeedingAction } from '@/lib/questions';
import type { RoomOutletContext } from './context';

/** The room's subpages, in nav order. `path` is relative to /rooms/:roomId. */
const ROOM_TABS: { path: string; label: string; feature?: 'contracts' }[] = [
  { path: '', label: 'Data room' },
  { path: 'processing', label: 'Processing' },
  { path: 'checklist', label: 'Checklist' },
  { path: 'buyers', label: 'Buyers & LOIs' },
  { path: 'qa', label: 'Q&A' },
  { path: 'risks', label: 'Risks & issues' },
  { path: 'contracts', label: 'Contracts', feature: 'contracts' },
  { path: 'agreements', label: 'Agreements' },
  { path: 'access', label: 'Access' },
  { path: 'activity', label: 'Activity' },
  { path: 'settings', label: 'Settings' },
];

/**
 * The room shell (owner asks #2/#3): a calm header — back link, room name, status
 * and role — over a tab bar of subpages, instead of one long wall of cards. The
 * index route is the actual Data room (what the room holds), so an admin lands on
 * the room itself with every configuration area one click away. Also hosts the
 * checklist-source dialog: opened once on arrival from room creation (router
 * state), and on demand from the Checklist tab.
 */
export function RoomLayout() {
  const { roomId } = useParams<{ roomId: string }>();
  const id = roomId ?? '';
  const room = useRoom(id);
  // Buyer questions waiting on the intermediary (new, or a seller draft to release).
  const questions = useRoomQuestions(id);
  const qaNeedsAction = questionsNeedingAction(questions.data);
  // Documents not finished every stage — server state, polled while a stage
  // runs — so the indicator is there from any room page, after a reload, on
  // another device, whether or not the upload panel is open.
  const processing = useRoomProcessingQueue(id, 'unfinished');
  const processingCounts = processing.data ? processingSummary(processing.data.counts) : null;
  const unfinished = processingCounts?.unfinished ?? 0;
  // Spins only while a stage runs; what is left only waits on a person.
  const stageRunning = (processingCounts?.inProgress ?? 0) > 0;
  // Rename is offered to the room's own admins (main or co-admin) only — the API's
  // gate. A firm admin opening a colleague's room from the list sees the name alone.
  const session = useSession();
  const isRoomAdmin = session.data?.rooms.some((r) => r.roomId === id && r.isAdmin) ?? false;
  // The sellers' ownership must total 100% before anyone can vote; say so on every
  // page of the room (nothing until it has loaded, nothing without a seller).
  const sellers = useRoomSellers(id);
  const plan = useTenantPlan();
  const location = useLocation();
  const navigate = useNavigate();
  const arrivedFromCreate = Boolean((location.state as RoomLocationState | null)?.setupChecklist);
  // Room setup after creation: the checklist source, then (optional) invite
  // the seller. Opened on demand from the Checklist tab it is the checklist
  // step alone.
  const [setupStep, setSetupStep] = useState<'checklist' | 'release' | 'invite' | null>(
    arrivedFromCreate ? 'checklist' : null,
  );
  const [newRoomSetup] = useState(arrivedFromCreate);
  // After "Release Phase 1 now?" → Yes: every phase's dates are typed in the
  // Checklist tab's phase plan, so go there with its first date field focused.
  const showPhasePlan = useCallback(
    () =>
      navigate(`/rooms/${id}/checklist`, {
        state: { focusPhaseDates: true } satisfies RoomLocationState,
      }),
    [navigate, id],
  );

  // Consume the one-shot router state so a reload / back-forward doesn't reopen
  // the dialog (BrowserRouter persists history state across reloads).
  useEffect(() => {
    if (arrivedFromCreate) navigate(location.pathname, { replace: true, state: null });
  }, [arrivedFromCreate, location.pathname, navigate]);

  const openChecklistSetup = useCallback(() => setSetupStep('checklist'), []);

  // Tabs never wrap — they scroll sideways — so bring the current tab into view
  // on arrival (a phone landing on Activity would otherwise not see it).
  const tabsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const current = tabsRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
    current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [location.pathname, room.data]);

  if (isUnauthorized(room.error)) return <Navigate to="/login" replace />;

  if (room.isPending) {
    return <p className={cn(containerClass, 'py-6 text-sm text-muted-foreground')}>Loading…</p>;
  }
  if (!room.data) {
    return (
      <div className={cn(containerClass, 'space-y-3 py-6')}>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:underline">
          <Icon name="arrowLeft" size={14} />
          All rooms
        </Link>
        <p className="text-sm text-destructive">
          {room.error ? room.error.message : 'This room could not be found.'}
        </p>
      </div>
    );
  }

  const data = room.data;
  const context: RoomOutletContext = { roomId: id, room: data, openChecklistSetup };

  const lifecycle = lifecycleChip(data.lifecycleState);
  // A new room continues to the seller invite (unless one is bound).
  const afterRelease = newRoomSetup && !data.sellerActorId ? 'invite' : null;
  const canWrite = data.lifecycleState === 'active' || data.lifecycleState === 'draft';

  return (
    <div className={cn(containerClass, 'pb-12')}>
      <div className="flex flex-col gap-2 pt-6">
        <Link
          to="/"
          className="inline-flex w-fit items-center gap-1.5 text-[13px] text-ink-2 hover:underline"
        >
          <Icon name="arrowLeft" size={14} />
          All rooms
        </Link>
        <RoomNameHeading
          roomId={id}
          name={data.name}
          // Archived rooms are read-only (the API answers 409); trashed never show here.
          canRename={
            isRoomAdmin && (data.lifecycleState === 'active' || data.lifecycleState === 'draft')
          }
        />
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
          <Chip tone={lifecycle.tone} icon={lifecycle.icon}>
            {LIFECYCLE_LABELS[data.lifecycleState] ?? data.lifecycleState}
          </Chip>
          {data.adminRole ? <span>You act as {ROOM_ADMIN_ROLE_LABELS[data.adminRole]}</span> : null}
          {unfinished > 0 ? (
            <Link
              to={`/rooms/${id}/processing`}
              className="inline-flex h-6 items-center gap-1 rounded-pill border border-st-processing-line bg-st-processing pl-2 pr-2.5 text-xs font-semibold text-st-processing-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="processing-indicator"
            >
              <Icon
                name={stageRunning ? 'spinner' : 'alert'}
                size={12}
                className={stageRunning ? 'animate-spin' : undefined}
              />
              Processing {unfinished}…
              <span className="sr-only">
                {' '}
                {unfinished === 1 ? 'document is' : 'documents are'} not finished — open Processing
              </span>
            </Link>
          ) : null}
          {unfinished > 0 ? <HelpTip id="admin.room.processing-indicator" /> : null}
          {/* The tab bar scrolls sideways (it would clip a popover), so the Q&A badge's
              "?" sits here with a link to the same place. */}
          {qaNeedsAction > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Link to={`/rooms/${id}/qa`} className="hover:underline">
                Q&amp;A: {qaNeedsAction} need{qaNeedsAction === 1 ? 's' : ''} your action
              </Link>
              <HelpTip id="admin.room.qa-badge" />
            </span>
          ) : null}
        </div>
        <nav ref={tabsRef} aria-label="Room sections" className="dr-scroll-x mt-2 border-b">
          <ul className="flex min-w-max gap-1 pr-6">
            {ROOM_TABS.filter((tab) => tab.feature !== 'contracts' || contractsEnabled()).map((tab) => (
              <li key={tab.path || 'index'}>
                <NavLink
                  to={tab.path ? `/rooms/${id}/${tab.path}` : `/rooms/${id}`}
                  end={tab.path === ''}
                  className={({ isActive }) =>
                    cn(
                      'relative inline-flex h-11 items-center whitespace-nowrap px-3 text-sm transition-colors',
                      isActive
                        ? 'font-semibold text-ink after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary'
                        : 'font-medium text-ink-2 hover:text-ink',
                    )
                  }
                >
                  {tab.label}
                  {tab.path === 'qa' && qaNeedsAction > 0 ? (
                    <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 text-[11px] font-semibold leading-none text-primary-foreground">
                      {qaNeedsAction}
                      <span className="sr-only"> need your action</span>
                    </span>
                  ) : null}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="pt-6">
        {sellers.data && sellers.data.sellers.length > 0 && sellers.data.warning ? (
          <SellerOwnershipWarning warning={sellers.data.warning} className="mb-4" />
        ) : null}
        {/* The free trial's guide, on the room's home (a co-admin has no plan read, so none). */}
        {plan.data?.isTrial && location.pathname.replace(/\/+$/, '') === `/rooms/${id}` ? (
          <TrialGuide rooms={[data]} compact />
        ) : null}
        <Outlet context={context} />
      </div>

      {setupStep === 'checklist' ? (
        <ChecklistSourceDialog
          roomId={id}
          // A checklist is not released on its own: the next step asks about Phase 1
          // (and says nothing when there is nothing to ask).
          onClose={() => setSetupStep(isRoomAdmin ? 'release' : afterRelease)}
        />
      ) : setupStep === 'release' ? (
        <PhaseReleaseFlow
          roomId={id}
          quietIfNothing
          onReleased={showPhasePlan}
          onClose={() => setSetupStep(afterRelease)}
        />
      ) : setupStep === 'invite' ? (
        <InviteSellerDialog roomId={id} onClose={() => setSetupStep(null)} />
      ) : isRoomAdmin && canWrite ? (
        <ReleasePromptOnOpen roomId={id} onReleased={showPhasePlan} />
      ) : null}
    </div>
  );
}

/** Unknown /rooms/:roomId/<x> → the room's Data room (keeps old/mistyped links
 * inside the room rather than bouncing to the rooms list). */
export function RoomIndexRedirect() {
  const { roomId } = useParams<{ roomId: string }>();
  return <Navigate to={`/rooms/${roomId ?? ''}`} replace />;
}
